import { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Send, Calendar, DollarSign, TrendingUp, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('monthly');
  const [recipientEmails, setRecipientEmails] = useState('');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/reports`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    
    try {
      const token = localStorage.getItem('vyrus_token');
      const emails = recipientEmails.split(',').map(e => e.trim()).filter(e => e);
      
      let url = `${BACKEND_URL}/api/reports/generate?report_type=${reportType}`;
      if (emails.length > 0) {
        emails.forEach(email => {
          url += `&recipient_emails=${encodeURIComponent(email)}`;
        });
      }
      
      const response = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(emails.length > 0 
        ? 'Report generated and sent to partners!' 
        : 'Report generated successfully!'
      );
      setDialogOpen(false);
      setRecipientEmails('');
      fetchReports();
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight" data-testid="reports-title">
            Partner Reports
          </h1>
          <p className="text-muted-foreground mt-1">Generate and send reports to partners</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="generate-report-button">
          <Plus className="h-4 w-4 mr-2" />
          Generate Report
        </Button>
      </div>

      {/* Report History */}
      {reports.length === 0 ? (
        <Card className="p-12 text-center bg-card border-border/50">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No reports generated yet</p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Generate Your First Report
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="bg-card border-border/50">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg capitalize">{report.report_type} Report</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Generated {formatDate(report.generated_at)} by {report.generated_by}
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary">
                    {report.report_type.toUpperCase()}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Total Events</p>
                    <p className="text-xl font-bold">{report.summary.total_events}</p>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <p className="text-xs text-muted-foreground">Revenue</p>
                    <p className="text-xl font-bold text-green-400">
                      {formatCurrency(report.summary.total_revenue)}
                    </p>
                  </div>
                  <div className="p-3 bg-red-500/10 rounded-lg">
                    <p className="text-xs text-muted-foreground">Expenses</p>
                    <p className="text-xl font-bold text-red-400">
                      {formatCurrency(report.summary.total_expenses)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${report.summary.net_profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <p className="text-xs text-muted-foreground">Net Profit</p>
                    <p className={`text-xl font-bold ${report.summary.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(report.summary.net_profit)}
                    </p>
                  </div>
                </div>

                {/* Staff and Influencer Costs */}
                {(report.summary.total_staff_costs > 0 || report.summary.total_influencer_owed > 0) && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {report.summary.total_staff_costs > 0 && (
                      <div className="p-3 bg-orange-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Staff Costs</p>
                        <p className="text-lg font-bold text-orange-400">
                          {formatCurrency(report.summary.total_staff_costs)}
                        </p>
                      </div>
                    )}
                    {report.summary.total_influencer_owed > 0 && (
                      <div className="p-3 bg-purple-500/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Influencer Owed</p>
                        <p className="text-lg font-bold text-purple-400">
                          {formatCurrency(report.summary.total_influencer_owed)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Show Type Breakdown */}
                {report.show_type_breakdown && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-purple-500/10 rounded-lg border-l-2 border-purple-500">
                      <p className="text-xs text-muted-foreground">Big Shows (Graphics)</p>
                      <p className="text-sm">{report.show_type_breakdown.vyrus_graphics?.count || 0} events</p>
                      <p className={`text-lg font-bold ${(report.show_type_breakdown.vyrus_graphics?.profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(report.show_type_breakdown.vyrus_graphics?.profit || 0)}
                      </p>
                    </div>
                    <div className="p-3 bg-cyan-500/10 rounded-lg border-l-2 border-cyan-500">
                      <p className="text-xs text-muted-foreground">Small Shows (Cards)</p>
                      <p className="text-sm">{report.show_type_breakdown.vyrus_trading_cards?.count || 0} events</p>
                      <p className={`text-lg font-bold ${(report.show_type_breakdown.vyrus_trading_cards?.profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(report.show_type_breakdown.vyrus_trading_cards?.profit || 0)}
                      </p>
                    </div>
                  </div>
                )}

                {report.top_performing_events && report.top_performing_events.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Top Performing Events</p>
                    <div className="space-y-2">
                      {report.top_performing_events.slice(0, 3).map((event, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">{idx + 1}. {event.name}</span>
                          <span className={event.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {formatCurrency(event.profit)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Report Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">Generate Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="reportType">Report Type</Label>
              <select
                id="reportType"
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground focus:outline-none focus:ring-1 focus:ring-primary h-11"
              >
                <option value="monthly">Monthly Report</option>
                <option value="quarterly">Quarterly Report</option>
                <option value="yearly">Yearly Report</option>
                <option value="custom">Custom Report</option>
              </select>
            </div>
            
            <div>
              <Label htmlFor="emails">Send to Partners (optional)</Label>
              <Input
                id="emails"
                type="text"
                placeholder="email1@example.com, email2@example.com"
                value={recipientEmails}
                onChange={(e) => setRecipientEmails(e.target.value)}
                className="bg-secondary/50 border-transparent focus:border-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Separate multiple emails with commas. Leave blank to just save the report.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerateReport} disabled={generating}>
                {generating ? 'Generating...' : (
                  <>
                    {recipientEmails ? <Send className="h-4 w-4 mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                    {recipientEmails ? 'Generate & Send' : 'Generate Report'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
